import { Button, Dialog, Field, Input, MenuButton, type MenuItem } from "@dl/web-core-js/ui";
import { Plus, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { customSpan, customYear, presetSelection, type PresetKey, type Selection } from "../pages/sales/periods";

/**
 * The periods a sales grid shows: the ones that are lit, and a menu to add one.
 *
 * Only what is selected is shown as a chip — nine presets in a row was a wall,
 * and most of them were off. The menu lists the presets in three groups and
 * ends with a custom year or span, which absorbs the old "Add period column"
 * dialog. On the analysis page a chip can be removed as long as one remains;
 * on the dashboard picking from the menu replaces the one chip there is.
 */
export function PeriodStrip({
  selected,
  onToggle,
  onRemove,
  single,
}: {
  selected: Selection[];
  onToggle: (selection: Selection) => void;
  onRemove: (key: string) => void;
  single?: boolean;
}) {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);

  const isOn = (key: string) => selected.some((entry) => entry.key === key);

  const group = (section: string, keys: PresetKey[]): MenuItem[] =>
    keys
      .filter((key) => single || !isOn(key))
      .map((key, index) => ({
        label: presetLabel(key, t),
        onClick: () => onToggle(presetSelection(key)),
        ...(index === 0 ? { section } : {}),
      }));

  const items: MenuItem[] = [
    ...group(t("period.section.current", "Current"), ["this-month", "this-quarter", "ytd"]),
    ...group(t("period.section.past", "Past"), ["last-month", "last-quarter", "last-year"]),
    ...group(t("period.section.rolling", "Rolling"), ["last-12-months", "last-30-days", "last-7-days"]),
    { label: t("analysis.custom", "Custom"), onClick: () => setAdding(true), section: t("period.section.other", "Other") },
  ];

  // One period at a time: the period is the button. A chip plus a "change"
  // button said the same thing twice, in a toolbar that has no room for it.
  if (single) {
    const only = selected[0];
    return (
      <>
        <MenuButton label={only ? selectionLabel(only, t) : t("analysis.add-period", "Add period")} items={items} variant="secondary" />
        {adding && (
          <AddPeriodDialog
            onClose={() => setAdding(false)}
            onAdd={(selection) => {
              onToggle(selection);
              setAdding(false);
            }}
          />
        )}
      </>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {selected.map((entry) => (
        <span
          key={entry.key}
          className="inline-flex items-center gap-1 rounded-full border border-accent bg-accent-soft py-0.5 pl-2.5 pr-1.5 text-[12px] leading-5 text-accent-strong"
        >
          {selectionLabel(entry, t)}
          {!single && selected.length > 1 && (
            <button
              type="button"
              onClick={() => onRemove(entry.key)}
              aria-label={t("button.remove", "Remove")}
              className="rounded-full p-0.5 text-accent hover:bg-accent hover:text-ink-on-accent"
            >
              <X className="size-3" />
            </button>
          )}
        </span>
      ))}

      <MenuButton label={t("analysis.add-period", "Add period")} icon={<Plus className="size-3.5" />} items={items} variant="secondary" />

      {adding && (
        <AddPeriodDialog
          onClose={() => setAdding(false)}
          onAdd={(selection) => {
            onToggle(selection);
            setAdding(false);
          }}
        />
      )}
    </div>
  );
}

function presetLabel(preset: PresetKey, t: (key: string, fallback: string) => string): string {
  switch (preset) {
    case "this-month":
      return t("period.this-month", "This month");
    case "this-quarter":
      return t("period.this-quarter", "This quarter");
    case "ytd":
      return t("period.ytd", "Year to date");
    case "last-month":
      return t("period.last-month", "Last month");
    case "last-quarter":
      return t("period.last-quarter", "Last quarter");
    case "last-year":
      return t("period.last-year", "Last year");
    case "last-12-months":
      return t("period.last-12-months", "Last 12 months");
    case "last-30-days":
      return t("period.last-30-days", "Last 30 days");
    case "last-7-days":
      return t("period.last-7-days", "Last 7 days");
  }
}

function selectionLabel(selection: Selection, t: (key: string, fallback: string) => string): string {
  if (!selection.custom) return presetLabel(selection.key as PresetKey, t);
  const { anchor } = selection;
  if (anchor.kind === "year") return String(anchor.year);
  if (anchor.kind === "days") return `${anchor.from} → ${anchor.to}`;
  return selection.key;
}

/** A calendar year, or an explicit range — the dialog the "Add period column" button used to open. */
function AddPeriodDialog({ onClose, onAdd }: { onClose: () => void; onAdd: (selection: Selection) => void }) {
  const { t } = useTranslation();
  const [year, setYear] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const usingYear = year !== "";
  const usingRange = from !== "" || to !== "";
  const rangeValid = /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(to) && from <= to;
  const valid = usingYear !== usingRange && (usingYear ? /^\d{4}$/.test(year) : rangeValid);

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("analysis.add-column", "Add a period")}
      description={t("analysis.add-column.description", "Pick a calendar year, or an explicit range, not both.")}
      size="sm"
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("button.cancel", "Cancel")}
          </Button>
          <Button
            variant="primary"
            disabled={!valid}
            onClick={() => onAdd(usingYear ? customYear(Number(year)) : customSpan(from, to))}
          >
            {t("button.add", "Add")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label={t("analysis.year", "Year")} htmlFor="period-year">
          <Input id="period-year" placeholder="2024" value={year} disabled={usingRange} onChange={(e) => setYear(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("period.from", "From")} htmlFor="period-from">
            <Input id="period-from" type="date" value={from} disabled={usingYear} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label={t("period.to", "To")} htmlFor="period-to">
            <Input id="period-to" type="date" value={to} disabled={usingYear} onChange={(e) => setTo(e.target.value)} />
          </Field>
        </div>
      </div>
    </Dialog>
  );
}
